import { IonButton } from '@ionic/react';
import {
  CakeIcon,
  ClockCounterClockwiseIcon,
  GenderNeuterIcon,
  PhoneOutgoingIcon,
  ScalesIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { getCoverageStatus, isDawerPet } from '../../../utils/coverage';
import { getEdad, getPeso } from '../../../utils/pet';
import './HomePetActionsGrid.scss';
import { HomePetActionsGridProps } from './types';

const DETAIL_ICON = { size: 24 } as const;
const ACTION_ICON = { size: 28 } as const;

const HomePetActionsGrid: React.FC<HomePetActionsGridProps> = ({ pet, actions }) => {
  const edad = getEdad(pet?.fecha_nacimiento);
  const peso = getPeso(pet);
  const sexo = pet?.sexo === 'M' ? 'Macho' : 'Hembra';

  if (isDawerPet(pet)) {
    const coverage = getCoverageStatus(pet);
    const hasCoverageDate = coverage.hasCoverageDate;
    const coverageExpired = hasCoverageDate && coverage.daysRemaining === 0;
    const coverageClassName = [
      'coverage-progress-card',
      `coverage-progress-card--${coverage.tone}`,
      coverageExpired ? 'coverage-progress-card--expired' : '',
      !hasCoverageDate ? 'coverage-progress-card--unknown' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const coverageBadge =
      !hasCoverageDate
        ? 'Fecha no disponible'
        : coverage.daysRemaining === 0
        ? 'Cobertura finalizada'
        : coverage.daysRemaining === 1
          ? 'Queda 1 día'
          : `Quedan ${coverage.daysRemaining} días`;

    return (
      <div className="home-pet-actions-grid home-pet-actions-grid--dawer">
        <div
          className="new-siniestro-card plan-upgrade-card"
          data-tut="reactour__dawer_plan"
        >
          <div className="plan-card-art" aria-hidden="true" />
          <h3 className="siniestro-title">Elegí tu plan!</h3>
          <p className="siniestro-description">
            Cargá tus datos de pago y dejá lista la cobertura de tu mascota.
          </p>
          <IonButton
            className="siniestro-btn"
            mode="ios"
            onClick={actions.openPlanPayment}
          >
            Elegir plan
          </IonButton>
        </div>

        <div className="pet-info-and-telemedicine" data-tut="reactour__dawer_pet_summary">
          <div className="pet-info-card">
            <div className="pet-name-section">
              <h2 className="pet-name">{pet?.nombre}</h2>
              <p className="pet-breed">{pet?.raza}</p>
            </div>

            <div className="pet-details pet-details--single">
              <div className="detail-item">
                <span className="svg-icon">
                  <GenderNeuterIcon {...DETAIL_ICON} />
                </span>
                <div>
                  <p className="detail-label">Sexo</p>
                  <p className="detail-value">{sexo}</p>
                </div>
              </div>
            </div>
          </div>

          <button
            className={`telemedicine-card${coverageExpired ? ' telemedicine-card--locked' : ''}`}
            onClick={actions.openTelemedicina}
            aria-disabled={coverageExpired}
          >
            <span className="telemedicine-icon">
              <PhoneOutgoingIcon {...ACTION_ICON} />
            </span>
            <span className="telemedicine-label">Telemedicina</span>
            {coverageExpired && (
              <span className="telemedicine-status">Cobertura vencida</span>
            )}
          </button>
        </div>

        <div className={coverageClassName} data-tut="reactour__dawer_coverage">
          <div className="coverage-header">
            <div>
              <p className="coverage-kicker">Cobertura gratuita</p>
              <h3 className="coverage-title">30 días sin cargo</h3>
            </div>
            <span className="coverage-days">{coverageBadge}</span>
          </div>

          <div
            className="coverage-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={coverage.progress}
          >
            <span className="coverage-fill" style={{ width: `${coverage.progress}%` }} />
          </div>

          <p className="coverage-copy">
            {!hasCoverageDate
              ? 'No recibimos la fecha de cobertura desde el backend.'
              : coverageExpired
              ? 'Elegí un plan de cobertura.'
              : 'Al finalizar, elegí un plan para mantener la cobertura activa.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-pet-actions-grid">
      <div data-tut="reactour__pet_info">
        {/* Pet Info Card */}
        <div className="pet-info-card">
          <div className="pet-header">
            <div className="pet-name-section">
              <h2 className="pet-name">{pet?.nombre}</h2>
              <p className="pet-breed">{pet?.raza}</p>
            </div>
            <div className="pet-age">
              <span className="age-icon">
                <CakeIcon {...DETAIL_ICON} weight="fill" />
              </span>
              <span>{edad}</span>
            </div>
          </div>

          <div className="pet-details">
            <div className="detail-item">
              <span className="svg-icon">
                <GenderNeuterIcon {...DETAIL_ICON} />
              </span>
              <div>
                <p className="detail-label">Sexo</p>
                <p className="detail-value">{sexo}</p>
              </div>
            </div>
            <div className="detail-item">
              <span className="svg-icon">
                <ScalesIcon size={26} />
              </span>
              <div>
                <p className="detail-label">Peso</p>
                <p className="detail-value"> {peso ? `${peso} Kg` : '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="action-btn" onClick={actions.openTelemedicina}>
            <span className="action-icon">
              <PhoneOutgoingIcon {...ACTION_ICON} />
            </span>
            <span className="action-label">Telemedicina</span>
          </button>
          <button className="action-btn" onClick={actions.openHistorial}>
            <span className="action-icon">
              <ClockCounterClockwiseIcon {...ACTION_ICON} weight="fill" />
            </span>
            <span className="action-label">Historial</span>
          </button>
        </div>
      </div>

      {/* Siniestro Card */}
      <div className="new-siniestro-card" data-tut="reactour__pet_siniestro">
        <h3 className="siniestro-title">¿Accidente, enfermedad o vacuna?</h3>
        <p className="siniestro-description">
          Informalo ahora para iniciar la gestión rápidamente.
        </p>
        <IonButton
          className="siniestro-btn"
          mode="ios"
          onClick={actions.openNuevoSiniestro}
        >
          Informar caso
        </IonButton>
      </div>
    </div>
  );
};

export default React.memo(HomePetActionsGrid);
